import { type Site } from '@/types/search';
import { useMemo } from 'react';

/**
 * At or below this many destinations, the full list *is* the featured grid: no
 * search field, no history chips, no group headers — those exist to tame a long
 * list, and imposing them on a handful of cards just adds chrome to scroll past.
 * Above it, the picker grows the search + grouping affordances.
 */
export const SHORT_LIST_MAX = 3;

export interface SiteGroupEntry {
  key: string;
  sites: Site[];
}

export interface SiteIndex {
  /** Every selectable site, in payload order. */
  all: Site[];
  /** True while the list is short enough to show as a bare card grid. */
  isShortList: boolean;
  /** Sites promoted to the card grid on a long list. */
  featured: Site[];
  /**
   * Genuinely flagged sites, for the quick-pick row when there's no search history.
   * Distinct from `featured`, which on a short list is simply every site — using it
   * here would print a chip for each card directly above that same card.
   */
  trending: Site[];
  /** Long-list sections. Falls back to the relation that actually has data. */
  groups: SiteGroupEntry[];
  /** Whether group headings are worth rendering at all. */
  showGroupHeadings: boolean;
  byMappingCode: (code?: string) => Site | undefined;
  filter: (query: string) => Site[];
}

/**
 * `city` is the intended grouping key but is unpopulated in practice, so it acts
 * as an override on top of the location relation, which does carry a name. Both
 * missing lands in "Khác" rather than dropping the site from the list.
 */
function groupKeyFor(site: Site): string {
  return site.city?.trim() || site.location?.name?.trim() || 'Khác';
}

function matches(site: Site, term: string): boolean {
  return (
    site.name.toLowerCase().includes(term) ||
    (site.location?.name?.toLowerCase().includes(term) ?? false) ||
    (site.address?.toLowerCase().includes(term) ?? false)
  );
}

export function useSiteIndex(sites: Site[]): SiteIndex {
  return useMemo(() => {
    const isShortList = sites.length <= SHORT_LIST_MAX;

    // `is_featured` is not reliably populated, so a long list falls back to
    // showing the head of the list rather than rendering an empty grid.
    // `is_featured` is unreliable on the site row, so a site also counts as
    // flagged when its location is — that relation is the one that's populated.
    const flagged = sites.filter((site) => site.is_featured || site.location?.is_featured);
    const featured = isShortList ? sites : flagged.length > 0 ? flagged.slice(0, 4) : sites.slice(0, 4);
    const trending = flagged.slice(0, 8);

    const grouped = new Map<string, Site[]>();
    for (const site of sites) {
      const key = groupKeyFor(site);
      const bucket = grouped.get(key);
      if (bucket) bucket.push(site);
      else grouped.set(key, [site]);
    }
    const groups = [...grouped.entries()].map(([key, groupSites]) => ({ key, sites: groupSites }));

    return {
      all: sites,
      isShortList,
      featured,
      trending,
      groups,
      // A single heading over the whole list labels nothing.
      showGroupHeadings: groups.length > 1,
      byMappingCode: (code?: string) => (code ? sites.find((site) => site.mapping_code === code) : undefined),
      filter: (query: string) => {
        const term = query.trim().toLowerCase();
        return term ? sites.filter((site) => matches(site, term)) : [];
      },
    };
  }, [sites]);
}
